import 'leaflet/dist/leaflet.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CircleMarker as LeafletCircleMarker, PathOptions } from 'leaflet';
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip as MapTooltip,
  useMap,
} from 'react-leaflet';
import {
  AlertTriangle,
  Building2,
  FilterX,
  LockKeyhole,
  MapPinned,
  RefreshCcw,
  Search,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  MetricCard,
  Spinner,
} from '@helios/ui';
import {
  realtimeErrorKind,
  type ProjectMapMarker,
  type ProjectMapResponse,
  type ProjectMapCohort,
  type RiskLevel,
} from '@/services/realtimeClient';
import { env } from '@/lib/env';
import { GoogleProjectLocations } from './GoogleProjectLocations';
import { ConfiguredGoogleProjectMap } from './ConfiguredGoogleProjectMap';
import { COHORT_PRESENTATION } from './projectMapCohorts';
import type { PortfolioMapScope } from '@/features/projects/portfolioMapScope';

interface ProjectMapPanelProps {
  data: ProjectMapResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => void;
  compact?: boolean;
  portfolioScope?: PortfolioMapScope;
}

interface MapFilters {
  cohort: ProjectMapCohort | '';
  search: string;
  risk: RiskLevel | '';
  sector: string;
  region: string;
}

const EMPTY_FILTERS: MapFilters = {
  cohort: '',
  search: '',
  risk: '',
  sector: '',
  region: '',
};

const RISK_OPTIONS: Array<{ value: RiskLevel | ''; label: string }> = [
  { value: '', label: 'All risk' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'LOW', label: 'Low' },
];

const SELECT_CLASS_NAME =
  'h-9 w-full rounded-full border border-input bg-background/70 px-3 text-sm text-foreground outline-none transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background';

const DEFAULT_CENTER: [number, number] = [39.5, -98.35];

const MAP_TILES = env.mapboxToken
  ? {
      url: `https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/512/{z}/{x}/{y}@2x?access_token=${encodeURIComponent(env.mapboxToken)}`,
      attribution:
        '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> <a href="https://www.mapbox.com/map-feedback/">Improve this map</a>',
      maxZoom: 22,
      tileSize: 512,
      zoomOffset: -1,
    }
  : {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
      tileSize: 256,
      zoomOffset: 0,
    };

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function ProjectMapPanel({
  data,
  isLoading,
  isError,
  error,
  onRetry,
  compact = false,
  portfolioScope,
}: ProjectMapPanelProps) {
  const [filters, setFilters] = useState<MapFilters>(EMPTY_FILTERS);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const markers = useMemo(
    () => (Array.isArray(data?.markers) ? data.markers : []),
    [data?.markers],
  );
  const sectors = useMemo(() => uniqueValues(markers.map((marker) => marker.sector)), [markers]);
  const regions = useMemo(
    () => uniqueValues(markers.map((marker) => marker.location.state)),
    [markers],
  );
  const filteredMarkers = useMemo(
    () => markers.filter((marker) => markerMatches(marker, filters)),
    [filters, markers],
  );
  const hasFilters = Boolean(
    filters.cohort || filters.search || filters.risk || filters.sector || filters.region,
  );
  const selectedMarker = markers.find((marker) => marker.id === selectedMarkerId) ?? null;
  const selectMarker = useCallback((marker: ProjectMapMarker) => {
    setSelectedMarkerId(marker.id);
  }, []);

  const setFilter = <Key extends keyof MapFilters>(key: Key, value: MapFilters[Key]) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  if (isLoading) {
    return <LoadingCard label="Loading project map…" />;
  }

  if (isError || !data || !Array.isArray(data.markers) || !data.metrics || !data.map) {
    const kind = realtimeErrorKind(error);
    const title =
      kind === 'permission'
        ? 'Project map access restricted'
        : kind === 'offline'
          ? 'Project map offline'
          : 'Project map unavailable';
    const description =
      kind === 'permission'
        ? 'Your current role cannot read tenant project locations.'
        : kind === 'offline'
          ? 'The API is unreachable. The map will recover automatically when connectivity returns.'
          : 'The tenant-scoped project map endpoint did not return a realtime map snapshot.';

    return (
      <ErrorState
        title={title}
        description={description}
        action={
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            <RefreshCcw className="h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        }
      />
    );
  }

  if (markers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPinned className="text-primary h-4 w-4" aria-hidden="true" />
            Project Map
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.cohorts ? (
            <CohortSummary
              data={data}
              selected={filters.cohort}
              onSelect={(value) => setFilter('cohort', value)}
            />
          ) : null}
          {portfolioScope ? (
            <EmptyState
              title="No recorded coordinates in this Portfolio cohort"
              description={`${portfolioScope.includedCount} included projects · ${portfolioScope.unmappedCount} need location review. Return to the same Portfolio scope to inspect those records.`}
            />
          ) : data.cohorts ? (
            <EmptyState
              title="No located records in these map cohorts"
              description="Only recorded statuses with qualifying source evidence enter these cohorts. Unlocated records and unavailable totals remain above; locations are never invented."
            />
          ) : env.googleMapsBrowserKey ? (
            <GoogleProjectLocations refreshKey={data.generatedAt} compact={compact} />
          ) : (
            <EmptyState
              title="No mapped projects"
              description="Create projects with latitude and longitude to populate the realtime map. Google Maps can locate stored project cities after it is configured."
            />
          )}
        </CardContent>
      </Card>
    );
  }

  const forecastVariance = decimalNumber(data.metrics.forecastVariance);

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPinned className="text-primary h-4 w-4" aria-hidden="true" />
              Realtime Project Map
            </CardTitle>
            <p className="text-muted-foreground mt-1 text-sm">
              {data.tenant?.name ?? 'Current tenant'} · updated {formatTime(data.generatedAt)}
            </p>
          </div>
          <Badge variant={data.metrics.criticalProjects > 0 ? 'critical' : 'success'}>
            {data.metrics.criticalProjects} critical
          </Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.cohorts ? (
            <CohortSummary
              data={data}
              selected={filters.cohort}
              onSelect={(value) => setFilter('cohort', value)}
            />
          ) : null}
          {!compact ? (
            <>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard
                  label={data.cohorts ? 'Located records' : 'Mapped Projects'}
                  value={data.cohorts ? markers.length : data.metrics.projects}
                  hint={data.coverage ? 'Measured project and bid subset' : 'Tenant-scoped'}
                  accent="info"
                />
                <MetricCard
                  label="Portfolio Value"
                  value={formatMoney(data.metrics.portfolioValue)}
                  hint={
                    data.metrics.portfolioValue === null ? 'Finance access required' : 'Mapped work'
                  }
                  accent="success"
                />
                <MetricCard
                  label="Open Alerts"
                  value={data.metrics.openAlerts}
                  hint="Open + acknowledged"
                  accent="warning"
                />
                <MetricCard
                  label="Forecast Variance"
                  value={formatMoney(data.metrics.forecastVariance)}
                  hint={
                    data.metrics.forecastVariance === null
                      ? 'Finance access required'
                      : 'FAC - current budget'
                  }
                  accent={
                    forecastVariance !== null && forecastVariance > 0 ? 'critical' : 'success'
                  }
                />
              </div>

              {!portfolioScope ? (
                <section
                  className="border-border bg-background/40 rounded-lg border p-3"
                  aria-label="Project map filters"
                >
                  <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-[minmax(200px,1fr)_120px_150px_120px_auto]">
                    <label>
                      <span className="sr-only">Search projects and locations</span>
                      <Input
                        value={filters.search}
                        onChange={(event) => setFilter('search', event.target.value)}
                        placeholder="Search project or location"
                        startAdornment={<Search className="h-4 w-4" aria-hidden="true" />}
                      />
                    </label>
                    <label>
                      <span className="sr-only">Risk level</span>
                      <select
                        className={SELECT_CLASS_NAME}
                        value={filters.risk}
                        onChange={(event) =>
                          setFilter('risk', event.target.value as MapFilters['risk'])
                        }
                      >
                        {RISK_OPTIONS.map((option) => (
                          <option key={option.value || 'all'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="sr-only">Sector</span>
                      <select
                        className={SELECT_CLASS_NAME}
                        value={filters.sector}
                        onChange={(event) => setFilter('sector', event.target.value)}
                      >
                        <option value="">All sectors</option>
                        {sectors.map((sector) => (
                          <option key={sector} value={sector}>
                            {sector}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="sr-only">Region</span>
                      <select
                        className={SELECT_CLASS_NAME}
                        value={filters.region}
                        onChange={(event) => setFilter('region', event.target.value)}
                      >
                        <option value="">All regions</option>
                        {regions.map((region) => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={!hasFilters}
                      className="justify-self-start sm:col-span-2 2xl:col-span-1"
                      onClick={() => setFilters(EMPTY_FILTERS)}
                    >
                      <FilterX className="h-4 w-4" aria-hidden="true" />
                      Clear
                    </Button>
                  </div>
                </section>
              ) : null}
            </>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.8fr)]">
            <MapCanvas
              markers={filteredMarkers}
              center={data.map.center}
              compact={compact}
              onSelect={selectMarker}
              refreshKey={data.generatedAt}
              cohorts={Boolean(data.cohorts)}
              filterIds={
                portfolioScope || hasFilters
                  ? filteredMarkers.map((marker) => marker.id)
                  : undefined
              }
            />
            <ProjectList
              markers={filteredMarkers}
              compact={compact}
              filtered={hasFilters}
              onClear={() => setFilters(EMPTY_FILTERS)}
              onSelect={selectMarker}
              recordedHealth={portfolioScope?.recordedHealth}
            />
          </div>
        </CardContent>
      </Card>

      <ProjectDrilldownDialog
        marker={selectedMarker}
        recordedHealth={portfolioScope?.recordedHealth}
        onOpenChange={(open) => {
          if (!open) setSelectedMarkerId(null);
        }}
      />
    </>
  );
}

function CohortSummary({
  data,
  selected,
  onSelect,
}: {
  data: ProjectMapResponse;
  selected: ProjectMapCohort | '';
  onSelect: (value: ProjectMapCohort | '') => void;
}) {
  const unlocated = (data.unlocated ?? []).filter(
    (record) => !selected || record.cohort === selected,
  );
  return (
    <section className="space-y-3" aria-label="Project and bid cohorts">
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {data.cohorts?.map((summary) => (
          <button
            key={summary.cohort}
            type="button"
            aria-pressed={selected === summary.cohort}
            className="border-border bg-background/40 focus-visible:ring-ring rounded-lg border p-3 text-left focus-visible:ring-2"
            onClick={() => onSelect(selected === summary.cohort ? '' : summary.cohort)}
          >
            <span className="text-foreground flex items-center gap-2 text-xs font-medium">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: COHORT_PRESENTATION[summary.cohort].color }}
              />
              {COHORT_PRESENTATION[summary.cohort].label}
            </span>
            <span className="text-foreground mt-1 block text-lg font-semibold">
              {summary.records ?? 'Unavailable'}
            </span>
            <span className="text-muted-foreground block text-xs">
              {summary.availability === 'complete'
                ? `${summary.plotted} located · ${summary.missingCoordinates} unlocated`
                : summary.availability === 'restricted'
                  ? 'Bid source access restricted'
                  : `${summary.measuredRecords} measured records · partial source coverage`}
            </span>
          </button>
        ))}
      </div>
      {data.coverage ? (
        <p className="text-muted-foreground text-xs">{data.coverage.basis}</p>
      ) : null}
      {unlocated.length ? (
        <details className="text-muted-foreground text-xs">
          <summary className="cursor-pointer">
            Recorded sources without verified coordinates ({unlocated.length} measured)
          </summary>
          <ul className="mt-2 max-h-48 space-y-2 overflow-auto">
            {unlocated.map((record) => (
              <li key={record.id}>
                {record.projectNumber} · {record.name} · {COHORT_PRESENTATION[record.cohort].label}{' '}
                ·{' '}
                {[record.city, record.state].filter(Boolean).join(', ') || 'Location not recorded'}{' '}
                · source: {record.source}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

function LoadingCard({ label }: { label: string }) {
  return (
    <Card>
      <CardContent className="flex min-h-[240px] items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3 text-sm">
          <Spinner className="text-primary h-4 w-4" aria-hidden="true" />
          {label}
        </div>
      </CardContent>
    </Card>
  );
}

function MapCanvas({
  markers,
  center,
  compact,
  onSelect,
  refreshKey,
  filterIds,
  cohorts = false,
}: {
  markers: ProjectMapMarker[];
  center: ProjectMapResponse['map']['center'];
  compact: boolean;
  onSelect: (marker: ProjectMapMarker) => void;
  refreshKey: string;
  filterIds?: string[];
  cohorts?: boolean;
}) {
  const initialCenter: [number, number] = center
    ? [center.latitude, center.longitude]
    : DEFAULT_CENTER;

  if (cohorts) {
    return (
      <ConfiguredGoogleProjectMap
        key={`cohort:${refreshKey}`}
        markers={markers.map((marker) => ({
          id: marker.id,
          title: `${marker.projectNumber}, ${marker.name} · ${marker.cohort ? COHORT_PRESENTATION[marker.cohort].label : 'Project'}`,
          latitude: marker.location.latitude,
          longitude: marker.location.longitude,
          riskLevel: marker.riskLevel,
          pinColor: marker.cohort ? COHORT_PRESENTATION[marker.cohort].color : undefined,
        }))}
        onSelect={(id) => {
          const marker = markers.find((record) => record.id === id);
          if (marker) onSelect(marker);
        }}
        className={compact ? 'h-[320px]' : undefined}
      />
    );
  }
  if (env.googleMapsBrowserKey) {
    return (
      <GoogleProjectLocations refreshKey={refreshKey} compact={compact} filterIds={filterIds} />
    );
  }

  return (
    <div className="min-w-0 space-y-2">
      <div
        className={
          compact
            ? 'border-border bg-background h-[320px] overflow-hidden rounded-lg border'
            : 'border-border bg-background h-[360px] overflow-hidden rounded-lg border sm:h-[420px] xl:h-[500px]'
        }
        role="region"
        aria-label="Interactive project location map"
      >
        <MapContainer
          center={initialCenter}
          zoom={4}
          minZoom={2}
          scrollWheelZoom
          className="bg-background h-full w-full"
        >
          <TileLayer
            // Mapbox's dark-v11 is already dark; only the light OSM fallback gets the dark treatment.
            className={env.mapboxToken ? undefined : 'helios-osm-tiles'}
            attribution={MAP_TILES.attribution}
            url={MAP_TILES.url}
            maxZoom={MAP_TILES.maxZoom}
            tileSize={MAP_TILES.tileSize}
            zoomOffset={MAP_TILES.zoomOffset}
          />
          <MapViewport markers={markers} fallbackCenter={initialCenter} />
          {markers.map((marker) => (
            <AccessibleProjectMarker key={marker.id} marker={marker} onSelect={onSelect} />
          ))}
        </MapContainer>
      </div>
      <p className="text-muted-foreground text-[11px]">
        Google Maps not configured · {env.mapboxToken ? 'Mapbox' : 'OpenStreetMap'} fallback ·
        Coordinates: ConstructionOS project records
      </p>
    </div>
  );
}

function MapViewport({
  markers,
  fallbackCenter,
}: {
  markers: ProjectMapMarker[];
  fallbackCenter: [number, number];
}) {
  const map = useMap();
  const fallbackLatitude = fallbackCenter[0];
  const fallbackLongitude = fallbackCenter[1];

  useEffect(() => {
    if (markers.length === 0) {
      map.setView([fallbackLatitude, fallbackLongitude], 4, { animate: false });
      return;
    }

    if (markers.length === 1) {
      const marker = markers[0];
      map.setView([marker.location.latitude, marker.location.longitude], 8, { animate: false });
      return;
    }

    const latitudes = markers.map((marker) => marker.location.latitude);
    const longitudes = markers.map((marker) => marker.location.longitude);
    map.fitBounds(
      [
        [Math.min(...latitudes), Math.min(...longitudes)],
        [Math.max(...latitudes), Math.max(...longitudes)],
      ],
      { animate: false, maxZoom: 9, padding: [32, 32] },
    );
  }, [fallbackLatitude, fallbackLongitude, map, markers]);

  return null;
}

function AccessibleProjectMarker({
  marker,
  onSelect,
}: {
  marker: ProjectMapMarker;
  onSelect: (marker: ProjectMapMarker) => void;
}) {
  const layerRef = useRef<LeafletCircleMarker | null>(null);
  const label = `Open details for ${marker.projectNumber}, ${marker.name}`;

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    let element: Element | undefined;
    const handleKeyDown = (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
        keyboardEvent.preventDefault();
        onSelect(marker);
      }
    };
    const bindElement = () => {
      element = layer.getElement();
      if (!element) return;
      element.setAttribute('tabindex', '0');
      element.setAttribute('role', 'button');
      element.setAttribute('aria-label', label);
      element.addEventListener('keydown', handleKeyDown);
    };

    bindElement();
    layer.on('add', bindElement);

    return () => {
      layer.off('add', bindElement);
      element?.removeEventListener('keydown', handleKeyDown);
    };
  }, [label, marker, onSelect]);

  return (
    <CircleMarker
      ref={layerRef}
      center={[marker.location.latitude, marker.location.longitude]}
      radius={marker.riskLevel === 'CRITICAL' ? 10 : 8}
      pathOptions={markerPathOptions(marker.riskLevel, marker.cohort)}
      eventHandlers={{ click: () => onSelect(marker) }}
    >
      <MapTooltip direction="top" offset={[0, -6]} opacity={1}>
        <span className="font-semibold">{marker.projectNumber}</span>
        <br />
        <span>{marker.name}</span>
      </MapTooltip>
    </CircleMarker>
  );
}

function ProjectList({
  markers,
  compact,
  filtered,
  onClear,
  onSelect,
  recordedHealth,
}: {
  markers: ProjectMapMarker[];
  compact: boolean;
  filtered: boolean;
  onClear: () => void;
  onSelect: (marker: ProjectMapMarker) => void;
  recordedHealth?: Readonly<Record<string, number | null>>;
}) {
  if (markers.length === 0) {
    return (
      <EmptyState
        title={filtered ? 'No projects match the current filters' : 'No mapped projects'}
        description={
          filtered
            ? 'Adjust or clear the project, location, risk, sector, and region filters.'
            : 'Create projects with valid geographic coordinates to populate this list.'
        }
        action={
          filtered ? (
            <Button type="button" variant="outline" size="sm" onClick={onClear}>
              <FilterX className="h-4 w-4" aria-hidden="true" />
              Clear filters
            </Button>
          ) : undefined
        }
      />
    );
  }

  const visible = compact ? markers.slice(0, 4) : markers;
  return (
    <div
      className={
        compact
          ? 'border-border max-h-[320px] overflow-auto rounded-lg border'
          : 'border-border max-h-[500px] overflow-auto rounded-lg border'
      }
      aria-label="Mapped projects"
    >
      {visible.map((marker) => {
        const budgetVariance = decimalNumber(marker.financials?.budgetVariance ?? null);
        return (
          <button
            key={marker.id}
            type="button"
            className="border-border bg-background/40 hover:bg-secondary/60 focus-visible:ring-ring block w-full border-b p-3 text-left transition-colors last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
            onClick={() => onSelect(marker)}
            aria-label={`Open details for ${marker.projectNumber}, ${marker.name}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-semibold">{marker.name}</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {marker.projectNumber} · {formatLocation(marker)}
                </p>
              </div>
              <Badge variant={riskVariant(marker.riskLevel)}>{formatEnum(marker.riskLevel)}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <MiniMetric
                label={recordedHealth ? 'Recorded model' : 'Health'}
                value={
                  recordedHealth
                    ? String(recordedHealth[marker.id] ?? 'Unavailable')
                    : String(marker.healthScore ?? 'Unavailable')
                }
              />
              <MiniMetric
                label="Value"
                value={formatMoney(marker.financials?.contractValue ?? null)}
              />
              <MiniMetric label="Alerts" value={String(marker.activeAlerts ?? 'Unavailable')} />
            </div>
            {budgetVariance !== null && budgetVariance > 0 ? (
              <p className="text-critical mt-2 flex items-center gap-1.5 text-xs">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                {formatMoney(marker.financials?.budgetVariance ?? null)} forecast exposure
              </p>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function ProjectDrilldownDialog({
  marker,
  onOpenChange,
  recordedHealth,
}: {
  marker: ProjectMapMarker | null;
  onOpenChange: (open: boolean) => void;
  recordedHealth?: Readonly<Record<string, number | null>>;
}) {
  return (
    <Dialog open={marker !== null} onOpenChange={onOpenChange}>
      {marker ? (
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="mb-1 flex items-center gap-2">
              <Building2 className="text-primary h-4 w-4" aria-hidden="true" />
              <Badge variant={riskVariant(marker.riskLevel)}>
                {formatEnum(marker.riskLevel)} risk
              </Badge>
            </div>
            <DialogTitle>{marker.name}</DialogTitle>
            <DialogDescription>
              {marker.projectNumber} · {formatLocation(marker)}
            </DialogDescription>
          </DialogHeader>

          <dl className="grid gap-3 sm:grid-cols-2">
            <ProjectDetail label="Status" value={formatEnum(marker.status)} />
            <ProjectDetail label="Phase" value={formatEnum(marker.phase)} />
            {marker.cohort ? (
              <ProjectDetail label="Cohort" value={COHORT_PRESENTATION[marker.cohort].label} />
            ) : null}
            {marker.source ? (
              <ProjectDetail
                label="Native source"
                value={`${marker.source} · ${marker.sourceId}`}
              />
            ) : null}
            <ProjectDetail
              label={recordedHealth ? 'Recorded model score' : 'Health score'}
              value={
                recordedHealth
                  ? String(recordedHealth[marker.id] ?? 'Unavailable')
                  : String(marker.healthScore ?? 'Unavailable')
              }
            />
            <ProjectDetail
              label="Open alerts"
              value={String(marker.activeAlerts ?? 'Unavailable')}
            />
            <ProjectDetail
              label="Coordinates"
              value={`${marker.location.latitude.toFixed(4)}, ${marker.location.longitude.toFixed(4)}`}
            />
            <ProjectDetail
              label="Completion"
              value={formatDate(marker.schedule.substantialCompletion)}
            />
          </dl>

          {marker.financials ? (
            <section className="border-border mt-5 border-t pt-4" aria-label="Project financials">
              <h3 className="text-foreground text-sm font-semibold">Financial position</h3>
              <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                <ProjectDetail
                  label="Contract value"
                  value={formatMoney(marker.financials.contractValue)}
                />
                <ProjectDetail
                  label="Current budget"
                  value={formatMoney(marker.financials.budgetCurrent)}
                />
                <ProjectDetail
                  label="Actual cost"
                  value={formatMoney(marker.financials.actualCost)}
                />
                <ProjectDetail
                  label="Forecast at completion"
                  value={formatMoney(marker.financials.forecastAtCompletion)}
                />
                <ProjectDetail
                  label="Forecast variance"
                  value={formatMoney(marker.financials.budgetVariance)}
                />
              </dl>
            </section>
          ) : (
            <div className="border-border bg-background/50 mt-5 flex items-start gap-3 rounded-lg border p-3">
              <LockKeyhole
                className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="text-foreground text-sm font-medium">
                  {marker.source && marker.source !== 'project'
                    ? 'Bid financials unavailable'
                    : 'Financial details restricted'}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {marker.source && marker.source !== 'project'
                    ? 'Bid locations and recorded statuses do not establish a contract, budget, payment or verified award amount.'
                    : 'Project budget read permission is required for financial values.'}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

function ProjectDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border bg-background/50 rounded-lg border px-3 py-2">
      <dt className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
        {label}
      </dt>
      <dd className="text-foreground mt-1 text-sm font-medium">{value}</dd>
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border bg-card/60 rounded-lg border px-2 py-1.5">
      <p className="text-muted-foreground text-[10px] uppercase tracking-wide">{label}</p>
      <p className="text-foreground mt-0.5 truncate font-medium">{value}</p>
    </div>
  );
}

function markerMatches(marker: ProjectMapMarker, filters: MapFilters): boolean {
  if (filters.cohort && marker.cohort !== filters.cohort) return false;
  if (filters.risk && marker.riskLevel !== filters.risk) return false;
  if (filters.sector && marker.sector !== filters.sector) return false;
  if (filters.region && marker.location.state !== filters.region) return false;

  const search = filters.search.trim().toLowerCase();
  if (!search) return true;

  return [
    marker.projectNumber,
    marker.name,
    marker.clientName,
    marker.sector,
    marker.location.city,
    marker.location.state,
    marker.location.latitude.toFixed(4),
    marker.location.longitude.toFixed(4),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase()
    .includes(search);
}

function uniqueValues(values: Array<string | null>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort();
}

function markerPathOptions(risk: RiskLevel | null, cohort?: ProjectMapCohort): PathOptions {
  const colors: Record<RiskLevel, { color: string; fillColor: string }> = {
    CRITICAL: { color: '#fecaca', fillColor: '#ef4444' },
    HIGH: { color: '#fde68a', fillColor: '#f59e0b' },
    MODERATE: { color: '#bae6fd', fillColor: '#38bdf8' },
    LOW: { color: '#bbf7d0', fillColor: '#22c55e' },
  };
  return {
    ...(cohort
      ? { color: COHORT_PRESENTATION[cohort].color, fillColor: COHORT_PRESENTATION[cohort].color }
      : risk
        ? colors[risk]
        : { color: '#cbd5e1', fillColor: '#94a3b8' }),
    fillOpacity: 0.82,
    opacity: 1,
    weight: 2,
  };
}

function riskVariant(risk: RiskLevel | null) {
  if (risk === null) return 'outline';
  if (risk === 'CRITICAL') return 'critical';
  if (risk === 'HIGH') return 'warning';
  if (risk === 'MODERATE') return 'info';
  return 'success';
}

function decimalNumber(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMoney(value: string | null): string {
  const parsed = decimalNumber(value);
  return parsed === null ? 'Restricted' : money.format(parsed);
}

function formatLocation(marker: ProjectMapMarker): string {
  return (
    [marker.location.city, marker.location.state].filter(Boolean).join(', ') ||
    'Location unavailable'
  );
}

function formatDate(value: string | null): string {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function formatEnum(value: string | null): string {
  if (value === null) return 'Not recorded';
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase());
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
